import { arrayHasSameElements } from '../../core/utils/utils';
import {
	CONFIDENCE_THRESHOLD,
	DEMAND_LEVEL,
	IPolDemandLevel,
} from '../models/workflows.models';
import { IPolSetting, POL_TEST } from '../../operations/models/operations.models';

/**
 * Utilidades POL del editor de workflows (NIF-530).
 *
 * Abrir cuando: sanitizar settings legacy o resolver nivel de exigencia / etiqueta de scoring.
 * Sin dependencias Angular — puro y testeable.
 */

/** Pruebas POL retiradas (NIF-476); se filtran al hidratar settings legacy. */
const DEPRECATED_POL_TESTS: POL_TEST[] = [POL_TEST.LIFT_HEAD, POL_TEST.LOWER_HEAD];

/**
 * Elimina tests POL deprecados (giros verticales) de la lista recibida del API.
 *
 * @param tests - Lista cruda del setting; `undefined` → `[]`
 * @returns Solo pruebas aún soportadas en el editor
 */
export function sanitizePolAvailableTests(tests: POL_TEST[] | undefined): POL_TEST[] {
	return (tests ?? []).filter((test: POL_TEST) => !DEPRECATED_POL_TESTS.includes(test));
}

/**
 * Normaliza `total_test` legacy (p. ej. 4 tras retirar tests) al máximo actual (3).
 *
 * @param totalTest - Valor crudo del API o del form
 * @returns Entero en rango `[1, 3]`
 */
export function sanitizePolTotalTest(totalTest: number | undefined): number {
	const parsed = Number(totalTest);
	if (!Number.isFinite(parsed) || parsed < 1) {
		return 1;
	}
	// Máximo de pruebas POL configurables tras retirar giros verticales (NIF-476).
	return Math.min(parsed, 3);
}

/**
 * Clona el setting POL aplicando sanitize de `total_test` y `available_tests`.
 *
 * @param setting - Setting parcial o completo del detalle/API
 */
export function sanitizePolSetting(setting: IPolSetting | undefined): IPolSetting {
	return {
		...(setting ?? {}),
		total_test: sanitizePolTotalTest(setting?.total_test),
		available_tests: sanitizePolAvailableTests(setting?.available_tests),
	};
}

/**
 * Convierte la etiqueta de umbral del UI (`MAX` / `STANDARD`) al score numérico del API.
 *
 * @param threshold - `CONFIDENCE_THRESHOLD.MAX` → 7; resto → 6.8
 */
export function mapConfidenceThresholdToScore(threshold: string): number {
	return threshold === CONFIDENCE_THRESHOLD.MAX ? 7 : 6.8;
}

/**
 * Empareja el setting persistido con un preset de `demandLevels` (STANDARD / MAX_FLUID).
 * Si no coincide ningún preset → `DEMAND_LEVEL.CUSTOM`.
 *
 * Criterios (todos deben cuadrar tras sanitize):
 * - total_test, total_tries, type, max_time
 * - scoring numérico ≈ score del preset (tolerancia float)
 * - available_tests (sin MIDDLE_FACE en la comparación, se asume siempre)
 *
 * @param setting - Setting del detalle/API
 * @param demandLevels - Presets del helper (`demandLevelsPol`)
 */
export function selectPolDemandLevel(
	setting: IPolSetting,
	demandLevels: readonly IPolDemandLevel[],
): DEMAND_LEVEL {
	const sanitizedSetting = sanitizePolSetting(setting);
	const sanitizedAvailableTests = sanitizedSetting.available_tests ?? [];

	// Comparación laxa: el API a veces trae strings/números mezclados; MIDDLE_FACE no cuenta en el set.
	const demandLevelFound = demandLevels.find(
		(dl: IPolDemandLevel) =>
			+dl.total_test === +sanitizedSetting.total_test &&
			+dl.total_tries === +setting?.total_tries &&
			String(dl.type) === String(setting?.type) &&
			Math.abs(mapConfidenceThresholdToScore(dl.scoring) - Number(setting?.scoring ?? 0)) < 0.0001 &&
			+dl.max_time === (+setting?.max_time || 300) &&
			arrayHasSameElements(
				dl.available_tests.filter((t: POL_TEST) => t !== POL_TEST.MIDDLE_FACE),
				sanitizedAvailableTests,
			),
	);
	if (demandLevelFound) {
		return demandLevelFound.id;
	}
	return DEMAND_LEVEL.CUSTOM;
}

/**
 * Etiqueta de scoring para UI de detalle: score `7` → MAX; resto → STANDARD.
 *
 * @param setting - Setting con `scoring` numérico
 */
export function getPolScoringLabel(setting: IPolSetting): string {
	return String(Number(setting?.scoring)) === '7' ? CONFIDENCE_THRESHOLD.MAX : CONFIDENCE_THRESHOLD.STANDARD;
}

/**
 * Score numérico del API → umbral del form POL (`MAX` / `STANDARD` / `CUSTOM` + custom).
 *
 * @param score - `setting.scoring` del detalle
 */
export function mapPolScoreToFormThreshold(score: number): {
	scoring: string;
	scoring_custom: number;
} {
	const hasCustomScoring =
		Number.isFinite(score) && Math.abs(score - 7) >= 0.0001 && Math.abs(score - 6.8) >= 0.0001;
	return {
		scoring: hasCustomScoring
			? CONFIDENCE_THRESHOLD.CUSTOM
			: String(score) === '7'
				? CONFIDENCE_THRESHOLD.MAX
				: CONFIDENCE_THRESHOLD.STANDARD,
		scoring_custom: Number.isFinite(score) ? score : 7,
	};
}

/** Id plano de ISelect o valor crudo. */
function resolvePolSelectOrPlain(value: unknown): unknown {
	if (value && typeof value === 'object' && 'id' in value) {
		return (value as { id: unknown }).id;
	}
	return value;
}

/**
 * Valores del form POL necesarios para montar `IPolSetting` del payload.
 * Abrir cuando: `host.polSetting` / create-update (mismo criterio legacy del orquestador).
 */
export interface PolSettingFormSnapshot {
	demandType: unknown;
	totalTries: unknown;
	settingType: unknown;
	totalTest: unknown;
	scoringMode: unknown;
	scoringCustom: unknown;
	availableTests: unknown;
	maxTime: unknown;
}

/**
 * Construye `IPolSetting` desde el form (CUSTOM o preset de `demandLevels`).
 *
 * @param snapshot - Controles POL del editor
 * @param demandLevels - Presets UI (`demandLevelsPol`)
 * @returns Setting listo para API o `null` si el preset no existe
 */
export function buildPolSettingFromForm(
	snapshot: PolSettingFormSnapshot,
	demandLevels: readonly IPolDemandLevel[],
): IPolSetting | null {
	const type = resolvePolSelectOrPlain(snapshot.demandType) as DEMAND_LEVEL;

	if (type === DEMAND_LEVEL.CUSTOM) {
		const scoringMode = resolvePolSelectOrPlain(snapshot.scoringMode);
		const rawScoringValue = String(snapshot.scoringCustom ?? '')
			.trim()
			.replace(',', '.');
		const scoringValue = Number(rawScoringValue);
		const testsRaw = snapshot.availableTests;
		const available_tests = Array.isArray(testsRaw)
			? testsRaw
					.map((item: { id?: POL_TEST } | POL_TEST) =>
						typeof item === 'object' && item && 'id' in item && item.id ? item.id : item,
					)
					.filter((t: POL_TEST) => t !== POL_TEST.MIDDLE_FACE)
			: [];

		return {
			total_tries: snapshot.totalTries as number,
			type: resolvePolSelectOrPlain(snapshot.settingType) as IPolSetting['type'],
			total_test: snapshot.totalTest as number,
			scoring:
				scoringMode === CONFIDENCE_THRESHOLD.CUSTOM
					? Number.isFinite(scoringValue)
						? scoringValue
						: 7
					: scoringMode === CONFIDENCE_THRESHOLD.MAX
						? 7
						: 6.8,
			available_tests,
			max_time: Number(snapshot.maxTime),
		};
	}

	const demandLevelFound = demandLevels.find((demand: IPolDemandLevel) => demand.id === type);
	if (demandLevelFound) {
		return {
			total_tries: demandLevelFound.total_tries,
			type: demandLevelFound.type,
			total_test: demandLevelFound.total_test,
			scoring: demandLevelFound.scoring === CONFIDENCE_THRESHOLD.MAX ? 7 : 6.8,
			available_tests: demandLevelFound.available_tests.filter(
				(t: POL_TEST) => t !== POL_TEST.MIDDLE_FACE,
			),
			max_time: +demandLevelFound.max_time,
		};
	}
	return null;
}
